import { useEffect, useMemo, useRef, useState } from 'react'

import { Box, Spinner, Text } from '@chakra-ui/react'
import { Loader } from '@googlemaps/js-api-loader'

import { API_KEY } from '@core/utils/constants/constants'

type Position = {
    lat: number
    lng: number
}

type Props = {
    position?: Position | null
    zoom?: number
    height?: string
    width?: string
    mapId?: string
    markerTitle?: string
    borderRadius?: string
    showZoomControls?: boolean
    showLocationNavigation?: boolean
    onPreviousLocation?: () => void
    onNextLocation?: () => void
}

const DEFAULT_ZOOM = 6

const isValidPosition = (position?: Position | null): position is Position =>
    !!position && Number.isFinite(position.lat) && Number.isFinite(position.lng)

const GoogleMapPosition = ({
    position,
    zoom = DEFAULT_ZOOM,
    height = '240px',
    width = '100%',
    mapId = 'a2bb0b0a8f621b59',
    markerTitle,
    borderRadius = '12px',
    showZoomControls = true,
    showLocationNavigation = false,
    onPreviousLocation,
    onNextLocation,
}: Props) => {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const mapRef = useRef<google.maps.Map | null>(null)
    const markerRef = useRef<google.maps.Marker | null>(null)
    const resizeObserverRef = useRef<ResizeObserver | null>(null)
    const [isLoaded, setIsLoaded] = useState(false)
    const [hasLoadError, setHasLoadError] = useState(false)

    const loader = useMemo(
        () =>
            new Loader({
                apiKey: API_KEY,
                version: 'quarterly',
            }),
        []
    )

    useEffect(() => {
        let isMounted = true

        const loadMap = async () => {
            try {
                await loader.load()
                if (isMounted) {
                    setIsLoaded(true)
                }
            } catch {
                if (isMounted) {
                    setHasLoadError(true)
                }
            }
        }

        loadMap()

        return () => {
            isMounted = false
        }
    }, [loader])

    useEffect(() => {
        if (!isLoaded || !containerRef.current || !isValidPosition(position)) return

        const map =
            mapRef.current ||
            new window.google.maps.Map(containerRef.current, {
                center: position,
                zoom,
                disableDefaultUI: true,
                zoomControl: showZoomControls,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                rotateControl: false,
                scaleControl: false,
                panControl: false,
                ...(mapId ? { mapId } : {}),
            })

        mapRef.current = map
        map.setCenter(position)
        map.setZoom(zoom)

        if (markerRef.current) {
            markerRef.current.setMap(null)
        }

        markerRef.current = new window.google.maps.Marker({
            map,
            position,
            title: markerTitle,
        })

        if (!resizeObserverRef.current) {
            resizeObserverRef.current = new ResizeObserver(() => {
                if (!mapRef.current) return
                window.google.maps.event.trigger(mapRef.current, 'resize')
            })
            resizeObserverRef.current.observe(containerRef.current)
        }
    }, [isLoaded, mapId, markerTitle, position, showZoomControls, zoom])

    useEffect(() => {
        return () => {
            resizeObserverRef.current?.disconnect()
            markerRef.current?.setMap(null)
            resizeObserverRef.current = null
            markerRef.current = null
            mapRef.current = null
        }
    }, [])

    const fallbackText = hasLoadError
        ? 'Google Maps is unavailable'
        : !isValidPosition(position)
          ? 'Position is not available'
          : null

    return (
        <Box position={'relative'} w={width} h={height} borderRadius={borderRadius} overflow={'hidden'}>
            <Box ref={containerRef} w={'100%'} h={'100%'} />

            {showLocationNavigation && (
                <>
                    <Box
                        as="button"
                        type="button"
                        aria-label="Previous location"
                        onClick={onPreviousLocation}
                        position={'absolute'}
                        top={'50%'}
                        left={'16px'}
                        transform={'translateY(-50%)'}
                        zIndex={2}
                        width={'44px'}
                        height={'44px'}
                        borderRadius={'9999px'}
                        backgroundColor={'#F5F5F5'}
                        border={'none'}
                        boxShadow={'0 2px 10px rgba(12, 12, 13, 0.12)'}
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        color={'#77777C'}
                        fontSize={'20px'}
                        lineHeight={1}
                        _hover={{ backgroundColor: '#EAEAEB' }}
                    >
                        ←
                    </Box>
                    <Box
                        as="button"
                        type="button"
                        aria-label="Next location"
                        onClick={onNextLocation}
                        position={'absolute'}
                        top={'50%'}
                        right={'16px'}
                        transform={'translateY(-50%)'}
                        zIndex={2}
                        width={'44px'}
                        height={'44px'}
                        borderRadius={'9999px'}
                        backgroundColor={'#F5F5F5'}
                        border={'none'}
                        boxShadow={'0 2px 10px rgba(12, 12, 13, 0.12)'}
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        color={'#77777C'}
                        fontSize={'20px'}
                        lineHeight={1}
                        _hover={{ backgroundColor: '#EAEAEB' }}
                    >
                        →
                    </Box>
                </>
            )}

            {!isLoaded && !hasLoadError && (
                <Box
                    position={'absolute'}
                    inset={0}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    bg={'rgba(255, 255, 255, 0.7)'}
                >
                    <Spinner size={'sm'} />
                </Box>
            )}

            {fallbackText && (
                <Box
                    position={'absolute'}
                    inset={0}
                    display={'flex'}
                    alignItems={'center'}
                    justifyContent={'center'}
                    bg={'rgba(255, 255, 255, 0.88)'}
                    px={'16px'}
                    textAlign={'center'}
                >
                    <Text fontSize={'sm'} color={'text.secondary'}>
                        {fallbackText}
                    </Text>
                </Box>
            )}
        </Box>
    )
}

export default GoogleMapPosition
